import { useEffect, useState } from 'react';
import {
  Box, Card, Stack, Typography, Sheet, Table, Button, Chip,
  Modal, ModalDialog, ModalClose, Input, Select, Option, FormControl, FormLabel, CircularProgress,
} from '@mui/joy';
import { APIFinancePnlDeals, APIFinanceOperationCreate, APIFinanceCategories } from '../../../api';
import { fmtMoney, scrollTableSx, SectionCard } from './financeShared';

// ── Разбивка со столбиками: rows = [{ label, amount }] ──
export function BreakdownList({ rows, color = '#059669', emptyText = 'Нет данных' }) {
  const list = (rows || []).filter((r) => r && Number(r.amount) !== 0);
  const max = list.reduce((m, r) => Math.max(m, Math.abs(Number(r.amount) || 0)), 0) || 1;
  if (list.length === 0) {
    return <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>{emptyText}</Typography>;
  }
  return (
    <Stack spacing={0.75}>
      {list.map((r, i) => (
        <Box key={i}>
          <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.25 }}>
            <Typography level="body-sm" sx={{ minWidth: 0, pr: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {r.label}
            </Typography>
            <Typography level="body-sm" sx={{ fontWeight: 700, flexShrink: 0 }}>{fmtMoney(r.amount)}</Typography>
          </Stack>
          <Box sx={{ height: 6, borderRadius: 4, bgcolor: 'background.level1', overflow: 'hidden' }}>
            <Box sx={{ height: '100%', width: `${Math.max(2, (Math.abs(r.amount) / max) * 100)}%`, bgcolor: color, borderRadius: 4 }} />
          </Box>
        </Box>
      ))}
    </Stack>
  );
}

// ── Список сделок, формирующих Приход (с подсветкой ручных передвижений) ──
export function PnlDealsList({ token, dateFrom, dateTo }) {
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  const [manualOnly, setManualOnly] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => { setPage(1); }, [dateFrom, dateTo, manualOnly]);
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    setLoading(true);
    APIFinancePnlDeals({ date_from: dateFrom, date_to: dateTo, page, manual_only: manualOnly || undefined }, token)
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e) => console.error('PnlDealsList', e))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [token, dateFrom, dateTo, page, manualOnly]);

  const rows = data?.results || [];
  const pg = data?.pagination;

  return (
    <SectionCard
      title="Сделки за период"
      action={
        <Button size="sm" variant={manualOnly ? 'solid' : 'soft'} color={manualOnly ? 'warning' : 'neutral'}
          onClick={() => setManualOnly((v) => !v)} sx={{ borderRadius: 'lg' }}>
          Только ручные передвижения
        </Button>
      }
    >
      <Sheet variant="outlined" sx={{ borderRadius: 'lg', overflow: 'auto' }}>
        <Table borderAxis="both" stickyHeader hoverRow sx={scrollTableSx(860)}>
          <thead>
            <tr>
              <th style={{ width: 90 }}>Дата</th>
              <th>Клиент</th>
              <th>Услуги</th>
              <th style={{ width: 120, textAlign: 'right' }}>Основные</th>
              <th style={{ width: 120, textAlign: 'right' }}>Допы</th>
              <th style={{ width: 130, textAlign: 'right' }}>Выручка</th>
              <th style={{ width: 120, textAlign: 'center' }}>Ручное</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={7} style={{ textAlign: 'center' }}><CircularProgress size="sm" /></td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={7} style={{ textAlign: 'center', color: '#94a3b8' }}>Нет сделок за период</td></tr>
            )}
            {!loading && rows.map((d) => (
              <tr key={d.event_id} style={d.has_manual_touch ? { background: '#fff7ed' } : undefined}>
                <td>{(d.date_completed || '').slice(0, 10)}</td>
                <td>{d.client_name}</td>
                <td>{(d.services || []).join(', ') || '—'}</td>
                <td style={{ textAlign: 'right' }}>{fmtMoney(d.main_amount)}</td>
                <td style={{ textAlign: 'right' }}>{fmtMoney(d.addon_amount)}</td>
                <td style={{ textAlign: 'right', fontWeight: 700 }}>{fmtMoney(d.revenue)}</td>
                <td style={{ textAlign: 'center' }}>
                  {d.has_manual_touch ? (
                    <Stack direction="row" spacing={0.5} justifyContent="center" flexWrap="wrap">
                      {d.manual?.price && <Chip size="sm" color="warning" variant="soft">цена</Chip>}
                      {d.manual?.transfer && <Chip size="sm" color="warning" variant="soft">выезд</Chip>}
                      {d.manual?.revision && <Chip size="sm" color="danger" variant="soft">доработка</Chip>}
                      {d.manual?.rework && <Chip size="sm" color="danger" variant="soft">не принято</Chip>}
                    </Stack>
                  ) : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Sheet>

      {pg && pg.total_pages > 1 && (
        <Stack direction="row" spacing={1} justifyContent="center" alignItems="center" sx={{ mt: 1.5 }}>
          <Button size="sm" variant="soft" disabled={!pg.previous} onClick={() => setPage((p) => Math.max(1, p - 1))}>Назад</Button>
          <Typography level="body-sm">{pg.current_page} / {pg.total_pages}</Typography>
          <Button size="sm" variant="soft" disabled={!pg.next} onClick={() => setPage((p) => p + 1)}>Вперёд</Button>
        </Stack>
      )}
    </SectionCard>
  );
}

// ── Модалка ручного ввода прихода/расхода (создаёт операцию ДДС) ──
export function ManualEntryModal({ open, onClose, token, onSaved }) {
  const [direction, setDirection] = useState('out');
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 16));
  const [categoryId, setCategoryId] = useState(null);
  const [comment, setComment] = useState('');
  const [categories, setCategories] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !token) return;
    APIFinanceCategories({ active_only: 'true', direction }, token)
      .then((d) => setCategories(Array.isArray(d) ? d : []))
      .catch(() => setCategories([]));
    setCategoryId(null);
  }, [open, token, direction]);

  const save = async () => {
    if (!amount || Number(amount) <= 0) { setError('Укажите сумму'); return; }
    setSaving(true); setError(null);
    try {
      await APIFinanceOperationCreate({
        direction, amount: Number(amount),
        operation_date: new Date(date).toISOString(),
        category: categoryId || null, comment: comment || null,
      }, token);
      onSaved?.();
      onClose?.();
      setAmount(''); setComment('');
    } catch (e) {
      setError(e?.response?.data?.detail || 'Не удалось сохранить операцию');
    } finally { setSaving(false); }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog sx={{ borderRadius: 'xl', width: 420, maxWidth: '95vw' }}>
        <ModalClose />
        <Typography level="title-md" sx={{ fontWeight: 800, mb: 1 }}>Ручной ввод операции</Typography>
        <Stack spacing={1.25}>
          <FormControl>
            <FormLabel>Тип</FormLabel>
            <Select value={direction} onChange={(_, v) => v && setDirection(v)}>
              <Option value="in">Приход</Option>
              <Option value="out">Расход</Option>
            </Select>
          </FormControl>
          <FormControl>
            <FormLabel>Сумма, ₽</FormLabel>
            <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} />
          </FormControl>
          <FormControl>
            <FormLabel>Дата</FormLabel>
            <Input type="datetime-local" value={date} onChange={(e) => setDate(e.target.value)} />
          </FormControl>
          <FormControl>
            <FormLabel>Статья</FormLabel>
            <Select value={categoryId} onChange={(_, v) => setCategoryId(v)} placeholder="Без статьи">
              <Option value={null}>Без статьи</Option>
              {categories.map((c) => (
                <Option key={c.id} value={c.id}>
                  {c.name}{c.pnl_group_display ? ` · ${c.pnl_group_display}` : ''}
                </Option>
              ))}
            </Select>
          </FormControl>
          <FormControl>
            <FormLabel>Комментарий</FormLabel>
            <Input value={comment} onChange={(e) => setComment(e.target.value)} />
          </FormControl>
          {error && <Typography level="body-sm" color="danger">{error}</Typography>}
          <Button loading={saving} onClick={save} sx={{ borderRadius: 'lg', mt: 0.5 }}>Сохранить</Button>
        </Stack>
      </ModalDialog>
    </Modal>
  );
}